// Utility functions for calculating similarity scores on the client side
// This allows re-sorting comparables when subject property details change WITHOUT making new API calls

/**
 * Calculate similarity score between subject property and a comparable
 * @param {Object} subjectProperty - The subject property with edited details
 * @param {Object} comparable - A comparable property
 * @returns {number} Similarity score 0-100
 */
export const calculateSimilarityScore = (subjectProperty, comparable) => {
  if (!subjectProperty || !comparable) return 0;

  // Get raw data from comparable (could be in rawData or direct fields)
  const compData = comparable.rawData || comparable;

  let totalScore = 0;
  let totalWeight = 0;

  // Weight configuration (total = 100)
  const weights = {
    bedrooms: 25,
    bathrooms: 20,
    sqft: 30,
    yearBuilt: 15,
    stories: 10
  };

  // Bedroom similarity (25% weight)
  const subjectBeds = subjectProperty.bedrooms || subjectProperty.beds;
  const compBeds = compData.BedroomsTotal || compData.bedrooms || comparable.beds;
  if (subjectBeds && compBeds) {
    const diff = Math.abs(subjectBeds - compBeds);
    // Perfect match = full score, within 1 = partial, more than 2 = minimal
    const bedroomScore = diff === 0 ? 1 : diff === 1 ? 0.75 : diff === 2 ? 0.25 : 0;
    totalScore += weights.bedrooms * bedroomScore;
    totalWeight += weights.bedrooms;
  }

  // Bathroom similarity (20% weight)
  const subjectBaths = subjectProperty.bathrooms || subjectProperty.baths;
  const compBaths = compData.BathroomsDecimal || compData.BathroomsTotalInteger || compData.bathrooms || comparable.baths;
  if (subjectBaths && compBaths) {
    const diff = Math.abs(subjectBaths - compBaths);
    // Perfect match = full score, within 0.5 = high, within 1 = partial
    const bathScore = diff === 0 ? 1 : diff <= 0.5 ? 0.8 : diff <= 1 ? 0.5 : 0.2;
    totalScore += weights.bathrooms * bathScore;
    totalWeight += weights.bathrooms;
  }

  // Square footage similarity (30% weight) - most important
  const subjectSqft = subjectProperty.sqft || subjectProperty.livingArea;
  const compSqft = compData.LivingArea || compData.GrossLivingArea || compData.SquareFeet || comparable.livingArea;
  if (subjectSqft && compSqft) {
    const percentDiff = Math.abs(subjectSqft - compSqft) / subjectSqft;
    // Within 10% = excellent, 25% = good, 50% = fair
    const sqftScore = percentDiff <= 0.10 ? 1 : percentDiff <= 0.25 ? 0.7 : percentDiff <= 0.50 ? 0.3 : 0;
    totalScore += weights.sqft * sqftScore;
    totalWeight += weights.sqft;
  }

  // Year built similarity (15% weight)
  const subjectYear = subjectProperty.yearBuilt;
  const compYear = compData.YearBuilt || comparable.yearBuilt;
  if (subjectYear && compYear) {
    const yearDiff = Math.abs(subjectYear - compYear);
    // Same decade = excellent, within 20 years = good, within 30 years = fair
    const yearScore = yearDiff <= 5 ? 1 : yearDiff <= 10 ? 0.8 : yearDiff <= 20 ? 0.5 : yearDiff <= 30 ? 0.2 : 0;
    totalScore += weights.yearBuilt * yearScore;
    totalWeight += weights.yearBuilt;
  }

  // Stories similarity (10% weight)
  const subjectStories = subjectProperty.stories;
  const compStories = compData.StoriesTotal || compData.DerivedStories || comparable.stories;
  if (subjectStories && compStories) {
    const storyDiff = Math.abs(subjectStories - compStories);
    const storyScore = storyDiff === 0 ? 1 : storyDiff <= 0.5 ? 0.7 : storyDiff <= 1 ? 0.3 : 0;
    totalScore += weights.stories * storyScore;
    totalWeight += weights.stories;
  }

  // Normalize score to 0-100
  const normalizedScore = totalWeight > 0 ? (totalScore / totalWeight) * 100 : 0;

  return Math.round(normalizedScore);
};

/**
 * Re-sort comparables based on similarity to updated subject property
 * Does NOT make API calls - uses existing data
 * @param {Array} allComparables - All available comparables
 * @param {Object} subjectProperty - Updated subject property details
 * @param {number} displayLimit - Max number to return (or Infinity for all)
 * @returns {Array} Sorted and limited comparables
 */
export const resortComparablesBySubject = (allComparables, subjectProperty, displayLimit = 20) => {
  if (!allComparables || allComparables.length === 0) {
    return [];
  }

  // Calculate new similarity scores based on updated subject
  const rescoredComparables = allComparables.map(comp => ({
    ...comp,
    similarityScore: calculateSimilarityScore(subjectProperty, comp),
    // Keep original score for reference
    originalSimilarityScore: comp.similarityScore
  }));

  // Sort by new similarity score (highest first)
  const sorted = rescoredComparables.sort((a, b) => b.similarityScore - a.similarityScore);

  // Apply display limit
  const limited = displayLimit === Infinity ? sorted : sorted.slice(0, displayLimit);

  console.log(`🔄 Re-sorted ${allComparables.length} comparables by updated subject, showing ${limited.length}`);

  return limited;
};

/**
 * Check if radius has changed (requires new API call)
 * @param {number} currentRadius - Current search radius
 * @param {number} lastFetchedRadius - Last radius used for API fetch
 * @returns {boolean} True if API call needed
 */
export const isRadiusChanged = (currentRadius, lastFetchedRadius) => {
  if (!lastFetchedRadius) return true; // No previous fetch
  return parseFloat(currentRadius) !== parseFloat(lastFetchedRadius);
};

/**
 * Check if only display limit changed (no API call needed)
 * @param {number} newLimit - New display limit
 * @param {number} totalAvailable - Total available from last fetch
 * @returns {boolean} True if we can handle locally
 */
export const canHandleLocallyByLimit = (newLimit, totalAvailable) => {
  // If requesting less than or equal to what we have, handle locally
  return newLimit <= totalAvailable;
};
